// RadarChart 函数实现

import * as d3 from  "d3";
import { scaleOrdinal, scaleLinear } from 'd3-scale';
import { schemeCategory10 } from 'd3-scale-chromatic';
import { VisualizationTypes } from '../../../../../types/data';



interface RadarChartConfig {
    w: number;
    h: number;
    margin: { top: number; right: number; bottom: number; left: number };
    levels: number;
    maxValue: number;
    labelFactor: number;
    wrapWidth: number;
    opacityArea: number;
    dotRadius: number;
    opacityCircles: number;
    strokeWidth: number;
    roundStrokes: boolean;
    showLabel: boolean;
    color: Record<string, string> | d3.ScaleOrdinal<string, string, never>;
    // Index signature
    [key: string]: any;
}


export const createRadarChart = (id: string, data: VisualizationTypes.RadarChartTypes.DataList[], options?: Partial<RadarChartConfig>) => {
    // console.log("==> createRadarChart");
    // console.log("==> id", id);
    // console.log("==> data", data);
    // console.log("==> options", options);

    var cfg:RadarChartConfig = {
        w: 600,				//Width of the circle
        h: 600,				//Height of the circle
        margin: {top: 20, right: 20, bottom: 20, left: 20}, //The margins of the SVG
        levels: 4,				//How many levels or inner circles should there be drawn
        maxValue: 0, 			//What is the value that the biggest circle will represent
        labelFactor: 1.1, 	//How much farther than the radius of the outer circle should the labels be placed
        wrapWidth: 60, 		//The number of pixels after which a label needs to be given a new line
        opacityArea: 0.3, 	//The opacity of the area of the blob
        dotRadius: 2, 			//The size of the colored circles of each blog
        opacityCircles: 0.1, 	//The opacity of the circles of each blob
        strokeWidth: 2, 		//The width of the stroke around each blob
        roundStrokes: true,	//If true the area and stroke will follow a round path (cardinal-closed)
        showLabel: false,  // 是否显示每个轴的 label
        color: scaleOrdinal(schemeCategory10)	//Color function
       };

       //Put all of the options into a variable called cfg
       if('undefined' !== typeof options){
         for(var i in options){
           if('undefined' !== typeof options[i]){ cfg[i] = options[i]; }
         }//for i
       }//if

        // 计算最大值
        var maxValue = Math.max(
            cfg.maxValue ?? 0,
            d3.max(data, (blob: VisualizationTypes.RadarChartTypes.DataList) =>
                d3.max(blob.dataPointList, (o: VisualizationTypes.RadarChartTypes.DataPoint) => o.value) ?? 0
            ) ?? 0
        );

        // 向上取整到最近的整数，并增加额外的空隙（例如最大值的 10%），然后确保是 0.5 的倍数
        maxValue = Math.ceil(maxValue * 1.1 / 0.5) * 0.5;

        // Names of each axis
        var allAxis = data[0].dataPointList?.map((d: VisualizationTypes.RadarChartTypes.DataPoint) => d.axis) ?? [];

        var total = allAxis.length;
        var radius = Math.min(cfg.w/2, cfg.h/2) 	//Radius of the outermost circle
        // var Format = d3.format('%')		 	//Percentage formatting
        var Format = d3.format('.2f')			 	//Percentage formatting
        var angleSlice = Math.PI * 2 / total;		//The width in radians of each "slice"

        //Scale for the radius
        var rScale = scaleLinear()
           .range([0, radius])
           .domain([0, maxValue]);

        const axisValues: VisualizationTypes.RadarChartTypes.AxisValues = {}; // Object to hold arrays of values by axis

        // Populate axisValues with values for each axis
        data.forEach((blob) => {
            blob.dataPointList.forEach((point) => {
            if (!axisValues[point.axis]) {
                axisValues[point.axis] = [];
            }
            axisValues[point.axis].push(point.value);
            });
        });

        // Compute max for each axis
        const axisMaxValues: VisualizationTypes.RadarChartTypes.MaxAxisValues = {};
        // 向上取整到最近的整数，并增加额外的空隙（例如最大值的 10%），然后确保是 0.5 的倍数
        Object.keys(axisValues).forEach((axis) => {
            axisMaxValues[axis] =  Math.ceil(d3.max(axisValues[axis]) as number * 1.1 / 0.5) * 0.5
        });
        // console.log("axisMaxValues: ", axisMaxValues)

        const scalesDict: VisualizationTypes.RadarChartTypes.ScalesDict = {};

        Object.keys(axisMaxValues).forEach(axis => {
            scalesDict[axis] = d3.scaleLinear()
                            .range([0, radius])
                            .domain([0, axisMaxValues[axis]]);
        });

        // console.log("scalesDict: ", scalesDict)
        // console.log("radar chart data: ", data)

       /////////////////////////////////////////////////////////
       //////////// Create the container SVG and g /////////////
       /////////////////////////////////////////////////////////

       //Remove whatever chart with the same id/class was present before
       d3.select(id).select("svg").remove();

       //Initiate the radar chart SVG
       var svg = d3.select(id).append("svg")
               .attr("width",  cfg.w + cfg.margin.left + cfg.margin.right)
               .attr("height", cfg.h + cfg.margin.top + cfg.margin.bottom)
               .attr("class", "radar"+id)
               .style("background", "transparent") // 确保背景透明，不阻挡下层事件
               .style("pointer-events", "none"); // SVG 本身不拦截事件
       //Append a g element
       var g = svg.append("g")
               .attr("transform", "translate(" + (cfg.w/2 + cfg.margin.left) + "," + (cfg.h/2 + cfg.margin.top) + ")");

       /////////////////////////////////////////////////////////
       ////////// Glow filter for some extra pizzazz ///////////
       /////////////////////////////////////////////////////////

       //Filter for the outside glow
    //    var filter = g.append('defs').append('filter').attr('id','glow'),
    //        feGaussianBlur = filter.append('feGaussianBlur').attr('stdDeviation','2.5').attr('result','coloredBlur'),
    //        feMerge = filter.append('feMerge'),
    //        feMergeNode_1 = feMerge.append('feMergeNode').attr('in','coloredBlur'),
    //        feMergeNode_2 = feMerge.append('feMergeNode').attr('in','SourceGraphic');

       /////////////////////////////////////////////////////////
       /////////////// Draw the Circular grid //////////////////
       /////////////////////////////////////////////////////////

       //Wrapper for the grid & axes
       var axisGrid = g.append("g").attr("class", "axisWrapper");

       //Draw the background circles
       axisGrid.selectAll(".levels")
          .data(d3.range(1,(cfg.levels+1)).reverse())
          .enter()
           .append("circle")
           .attr("class", "gridCircle")
           .attr("r", function(d, i){return radius/cfg.levels*d;})
           .style("fill", "white")
           .style("stroke", "#D5D5CC")
        //    .style("fill-opacity", cfg.opacityCircles)
        //    .style("filter" , "url(#glow)");

       //Text indicating at what % each level is
    //    axisGrid.selectAll(".axisLabel")
    //       .data(d3.range(1,(cfg.levels+1)).reverse())
    //       .enter().append("text")
    //       .attr("class", "axisLabel")
    //       .attr("x", 4)
    //       .attr("y", function(d){return -d*radius/cfg.levels;})
    //       .attr("dy", "0.4em")
    //       .style("font-size", "6px")
    //       .attr("fill", "#737373")
    //       .text(function(d,i) { return Format(maxValue * d/cfg.levels); });

       /////////////////////////////////////////////////////////
       //////////////////// Draw the axes //////////////////////
       /////////////////////////////////////////////////////////

       //Create the straight lines radiating outward from the center
       var axis = axisGrid.selectAll(".axis")
           .data(allAxis)
           .enter()
           .append("g")
           .attr("class", "axis");
       //Append the lines
       axis.append("line")
           .attr("x1", 0)
           .attr("y1", 0)
           .attr("x2", function(d, i){ return rScale(maxValue*1) * Math.cos(angleSlice*i - Math.PI/2); })
           .attr("y2", function(d, i){ return rScale(maxValue*1) * Math.sin(angleSlice*i - Math.PI/2); })
           .attr("class", "line")
           .style("stroke", "#D5D5CC")
           .style("stroke-width", "1px");

       //Append the labels at each axis
       if(cfg.showLabel===true){
            axis.append("text")
                .attr("class", "legend")
                .style("font-size", "5px")
                .attr("text-anchor", "middle")
                .attr("dy", "0.35em")
                .attr("x", function(d, i){ return rScale(maxValue * cfg.labelFactor) * Math.cos(angleSlice*i - Math.PI/2); })
                .attr("y", function(d, i){ return rScale(maxValue * cfg.labelFactor) * Math.sin(angleSlice*i - Math.PI/2); })
                .text(function(d){return d})
                .call(wrap, cfg.wrapWidth);
       }

       /////////////////////////////////////////////////////////
       ///////////// Draw the radar chart blobs ////////////////
       /////////////////////////////////////////////////////////

       //The radial line function
       const radarLine = d3.lineRadial<VisualizationTypes.RadarChartTypes.DataPoint>()
                .curve(d3.curveLinearClosed) // Replaces .interpolate("linear-closed")
                // .radius(d => rScale(d.value))
                .radius(d => {
                    // Check if a specific scale for this axis exists in scalesDict
                    const specificScale = scalesDict[d.axis];
                    // If a specific scale is found, use it; otherwise, use rScale
                    return specificScale ? specificScale(d.value) : rScale(d.value);
                })
                .angle((d, i) => i * angleSlice);

       if(cfg.roundStrokes) {
        //    radarLine.interpolate("cardinal-closed");
            radarLine.curve(d3.curveCardinalClosed);
       }

       //Create a wrapper for the blobs
       var blobWrapper = g.selectAll(".radarWrapper")
           .data(data)
           .enter().append("g")
           .attr("class", "radarWrapper");

       //Append the backgrounds
       blobWrapper
           .append("path")
           .attr("class", "radarArea")
           .attr("d", function(d,i) { return radarLine(d.dataPointList); })
           .style("fill", function(d,i) {
                if (typeof cfg.color === "function") {
                    // If color is a ScaleOrdinal
                    return cfg.color(d.attr);
                } else {
                    // If color is a Record<string, string>
                    return cfg.color[d.attr];
                }
            })
           .style("fill-opacity", cfg.opacityArea)
           .style("pointer-events", "all")
        //    .style("cursor", "pointer")
           .on('mouseover', function (event, d){
               //Dim all blobs
            //    console.log("in mouseover .radarArea this: ", this)
            //    d3.selectAll(".radarArea")
            //        .transition().duration(200)
            //        .style("fill-opacity", 0.1);
            //    //Bring back the hovered over blob
            //    d3.select(this)
            //        .transition().duration(200)
            //        .style("fill-opacity", 0.7);

                // 准备 tooltip 的内容
                // var dataList = d3.select(this).datum() as DataList;
                // console.log("dataList: ", dataList)
                // // 添加标题行
                // tooltipText.append("tspan")
                // .attr("x", 5)
                // .attr("dy", "1em")
                // .style("font-weight", "bold")
                // .text(dataList.attr);

                // // 添加数据点
                // dataList.dataPointList.forEach((dp, index) => {
                //     tooltipText.append("tspan")
                //         .attr("x", 5)
                //         .attr("dy", `${0.05 * index + 1}em`)
                //         .text(`${dp.axis}: ${Format(dp.value)}`)
                //         .style("font-size", "6px");
                // });

                // // 获取文本的尺寸
                // var tooltipTextNode = tooltipText.node();
                // if (tooltipTextNode) {
                //     var textSize = tooltipTextNode.getBBox();

                //     // 设置矩形的尺寸以适应文本
                //     tooltipRect.attr("width", textSize.width + 10)
                //     .attr("height", textSize.height + 10);

                //     // 设置 tooltip 的位置
                //     var [newX, newY] = d3.pointer(event);
                //     newX -= textSize.width / 2;
                //     newY -= textSize.height + 10;
                //     tooltip.attr("transform", `translate(${newX}, ${newY})`);
                //     tooltip.attr("transform", `translate(${newX}, ${newY})`);

                //     // 显示 tooltip
                //     tooltip.transition().duration(200).style("opacity", 1);
                // }
           })
           .on('mouseout', function(){
               //Bring back all blobs
            //    d3.selectAll(".radarArea")
            //        .transition().duration(200)
            //        .style("fill-opacity", cfg.opacityArea);

                // tooltip.transition().duration(200)
                //    .style("opacity", 0);
           });

       //Create the outlines
       blobWrapper.append("path")
           .attr("class", "radarStroke")
           .attr("d", function(d,i) { return radarLine(d.dataPointList); })
           .style("stroke-width", cfg.strokeWidth + "px")
           .style("stroke", function(d,i) {
                if (typeof cfg.color === "function") {
                    // If color is a ScaleOrdinal
                    return cfg.color(d.attr);
                } else {
                    // If color is a Record<string, string>
                    return cfg.color[d.attr];
                }
            })
           .style("fill", "none")
           .style("filter" , "url(#glow)");

       //Append the circles
       blobWrapper.selectAll(".radarCircle")
           .data(function(d,i) { return d.dataPointList; })
           .enter().append("circle")
           .attr("class", "radarCircle")
           .attr("r", cfg.dotRadius)
        //    .attr("cx", function(d,i){ return rScale(d.value) * Math.cos(angleSlice*i - Math.PI/2); })
        //    .attr("cy", function(d,i){ return rScale(d.value) * Math.sin(angleSlice*i - Math.PI/2); })
           .attr("cx", function(d,i){
                const specificScale = scalesDict[d.axis]? scalesDict[d.axis]: rScale;
                return specificScale(d.value) * Math.cos(angleSlice*i - Math.PI/2);
            })
           .attr("cy", function(d,i){
                const specificScale = scalesDict[d.axis]? scalesDict[d.axis]: rScale;
                return specificScale(d.value) * Math.sin(angleSlice*i - Math.PI/2);
            })
           .style("fill", function(d) {
            if (typeof cfg.color === "function") {
                // If color is a ScaleOrdinal
                // console.log("d: ", d)
                return cfg.color(d.attr);
            } else {
                // If color is a Record<string, string>
                return cfg.color[d.attr];
            }
        })
           .style("fill-opacity", 0.8);

       /////////////////////////////////////////////////////////
       //////// Append invisible circles for tooltip ///////////
       /////////////////////////////////////////////////////////

       //Wrapper for the invisible circles on top
       var blobCircleWrapper = g.selectAll(".radarCircleWrapper")
           .data(data)
           .enter().append("g")
           .attr("class", "radarCircleWrapper");

       //Append a set of invisible circles on top for the mouseover pop-up
       blobCircleWrapper.selectAll(".radarInvisibleCircle")
           .data(function(d,i) { return d.dataPointList; })
           .enter().append("circle")
           .attr("class", "radarInvisibleCircle")
           .attr("r", cfg.dotRadius*1.5)
        //    .attr("cx", function(d,i){ return rScale(d.value) * Math.cos(angleSlice*i - Math.PI/2); })
        //    .attr("cy", function(d,i){ return rScale(d.value) * Math.sin(angleSlice*i - Math.PI/2); })
            .attr("cx", function(d,i){
                    const specificScale = scalesDict[d.axis]? scalesDict[d.axis]: rScale;
                    return specificScale(d.value) * Math.cos(angleSlice*i - Math.PI/2);
                })
            .attr("cy", function(d,i){
                    const specificScale = scalesDict[d.axis]? scalesDict[d.axis]: rScale;
                    return specificScale(d.value) * Math.sin(angleSlice*i - Math.PI/2);
                })
           .style("fill", "none")
           .style("pointer-events", "all") // 确保雷达图的交互点可以接收事件
           .style("cursor", "pointer") // 设置鼠标悬停时的光标为指针
           .on("mouseover", function(d,i) {
            //    var newX =  parseFloat(d3.select(this).attr('cx')) - 10;
            //    var newY =  parseFloat(d3.select(this).attr('cy')) - 10;
               // 获取绑定到当前元素的数据
                var dataPoint = d3.select(this).datum() as VisualizationTypes.RadarChartTypes.DataPoint;

                var tooltipContent = `${dataPoint.axis}: ${Format(dataPoint.value)}`;

                tooltipText.text(tooltipContent);

                var tooltipTextNode = tooltipText.node();
                if (tooltipTextNode) {
                    var textSize = tooltipTextNode.getBBox();

                    // 设置矩形的尺寸以适应文本
                    tooltipRect.attr("width", textSize.width + 10)
                    .attr("height", textSize.height + 5)
                    .style("stroke", function() {
                        if (typeof cfg.color === "function") {
                            // If color is a ScaleOrdinal
                            return cfg.color(dataPoint.attr);
                        } else {
                            // If color is a Record<string, string>
                            return cfg.color[dataPoint.attr];
                        }});

                    // 设置 tooltip 的位置
                    var newX = parseFloat(d3.select(this).attr('cx')) - textSize.width / 2;
                    var newY = parseFloat(d3.select(this).attr('cy')) - textSize.height - 10;
                    tooltip.attr("transform", `translate(${newX}, ${newY})`);

                    tooltip.transition().duration(200).style("opacity", 1);
                }

           })
           .on("mouseout", function(){
               tooltip.transition().duration(200)
                   .style("opacity", 0);
           });

       //Set up the small tooltip for when you hover over a circle
    //    var tooltip = g.append("text")
    //        .attr("class", "radar-tooltip")
    //        .style("opacity", 0);

        // 创建 tooltip 容器
        var tooltip = g.append("g")
        .attr("class", "radar-tooltip")
        .style("opacity", 0);

        // 向 tooltip 容器中添加矩形（用于背景和边框）
        var tooltipRect = tooltip.append("rect")
        .style("fill", "white")
        .style("stroke-width", "1px")
        .attr("rx", 5)  // 设置 x 轴方向的圆角半径
        .attr("ry", 5); // 设置 y 轴方向的圆角半径
        // .style("stroke", "#737373");

        // 向 tooltip 容器中添加文本
        var tooltipText = tooltip.append("text")
        .style("font-size", "8px")
        .attr("x", 5) // 略微偏移，以避免贴边
        .attr("y", 10); // 略微偏移，以避免贴边

       /////////////////////////////////////////////////////////
       /////////////////// Helper Function /////////////////////
       /////////////////////////////////////////////////////////

       //Taken from http://bl.ocks.org/mbostock/7555321
       //Wraps SVG text
       function wrap(text: d3.Selection<SVGTextElement, string, SVGGElement, unknown>, width: number) {
         text.each(function(this: SVGTextElement) {
           var text = d3.select(this),
               words = text.text().split(/\s+/).reverse(),
               word: string | undefined,
               line: string[] = [], // 显式声明类型
               lineNumber = 0,
               lineHeight = 1.4, // ems
               y = text.attr("y"),
               x = text.attr("x"),
               dy = parseFloat(text.attr("dy")),
               tspan = text.text(null).append("tspan").attr("x", x).attr("y", y).attr("dy", dy + "em");

           while (word = words.pop()) {
             line.push(word);
             tspan.text(line.join(" "));
             const node = tspan.node(); // 存储 node 的结果
             if (node && node.getComputedTextLength() > width) {
               line.pop();
               tspan.text(line.join(" "));
               line = [word];
               tspan = text.append("tspan").attr("x", x).attr("y", y).attr("dy", ++lineNumber * lineHeight + dy + "em").text(word);
             }
           }
         });
       }//wrap

};//RadarChart